import React, { Component, PropTypes } from 'react';
import ReactDOM from 'react-dom';
import Radium from 'radium';
import shallowCompare from 'react-addons-shallow-compare';
import { isEqual } from 'lodash';
import isMobile from 'ismobilejs';
import YoutubePlayer from 'react-youtube-player';
import VideoRemoteControlButton from './videoRemoteControlButton';

const { phone, tablet } = isMobile;
const mobile = (phone || tablet);

const styles = {
  videoContainer: {
    overflow: 'hidden',
    flex: '1 1 100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  }
};

@Radium
export default class YouTube extends Component {

  static propTypes = {
    youTubeId: PropTypes.string.isRequired,
    lowRes: PropTypes.bool,
    presentation: PropTypes.bool
  };

  static contextTypes = {
    presenter: PropTypes.bool.isRequired,
    setVideoState: PropTypes.func.isRequired,
    session: PropTypes.object.isRequired,
    hideHUD: PropTypes.func.isRequired,
    deckState: PropTypes.object.isRequired,
    i18n: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);
    this.boundHandleKeyPress = ::this._handleKeyPress;
    this.boundSetVideoSize = ::this.setVideoSize;
    this.state = {
      videoWidth: null,
      videoHeight: null,
      startingUp: true,
      videoState: {}
    };
  }

  componentDidMount() {
    const { session, presenter } = this.context;
    if (session &&
        !presenter &&
        (!session.videoState ||
         Object.keys(session.videoState).length === 0)) {
      this.setVideoState({
        playing: false,
        started: false,
        finished: false
      });
    }
    setTimeout(() => { this.setVideoSize(); }, 0);
    setTimeout(() => { this.setState({ startingUp: false }); }, 500);
    this._attachEvents();
  }

  componentWillReceiveProps (nextProps, nextContext) {
    if (this.context.session &&
        this.context.session.videoState &&
        nextContext.session &&
        nextContext.session.videoState &&
        nextContext.session.videoState.playing !==
        this.context.session.videoState.playing) {
      this.context.hideHUD();
    }
    const { session } = nextContext;
    this.setState({
      videoState: (session && session.videoState) ? session.videoState : {}
    });
  }

  shouldComponentUpdate (nextProps, nextState) {
    let shouldUpdate = shallowCompare(this, nextProps, nextState);
    if (!shouldUpdate) {
      shouldUpdate = !isEqual(nextState, this.state);
    }
    return shouldUpdate;
  }

  componentDidUpdate() {
    const {videoState} = this.state;
    const {youtubePlayer} = this.refs;

    if (videoState.seek && youtubePlayer) {
      youtubePlayer.player.getCurrentTime()
        .then(currentTime => {
          const newTime = currentTime + videoState.seek;
          youtubePlayer.player.seekTo(newTime, true);
        });
    }
  }

  componentWillUnmount() {
    this._detachEvents();
  }

  onPause() {
    const { session } = this.context;
    const videoState = (session && session.videoState)
      ? session.videoState : {};

    if (videoState.playing) {
      this.setVideoState({
        playing: false,
        started: videoState.started,
        finished: videoState.finished
      });
    }
  }

  onPlay() {
    const { session } = this.context;
    const videoState = (session && session.videoState)
      ? session.videoState : {};

    if (!videoState.playing) {
      this.setVideoState({
        playing: true,
        started: videoState.started,
        finished: videoState.finished
      });
    }
  }

  setVideoSize() {
    const aspectRatio = 16.0 / 9.0;
    const videoContainer = ReactDOM.findDOMNode(this.refs.videoContainer);

    if (!videoContainer) {
      return;
    }

    if (mobile && this.resizeCount > 0) {
      return;
    }
    this.setState({ videoWidth: 0, videoHeight: 0 });

    let videoWidth = videoContainer.offsetWidth;
    let videoHeight = videoContainer.offsetHeight;
    if (videoHeight * aspectRatio < videoContainer.offsetWidth) {
      videoWidth = videoHeight * aspectRatio;
    }
    if (videoWidth / aspectRatio < videoContainer.offsetHeight) {
      videoHeight = videoWidth / aspectRatio;
    }
    this.setState({ videoWidth, videoHeight });
    this.resizeCount = (this.resizeCount || 0) + 1;
  }

  setVideoState (videoState) {
    this.context.setVideoState(videoState);
  }

  _attachEvents () {
    if (!this.context.presenter) {
      window.addEventListener('keydown', this.boundHandleKeyPress);
    }
    window.addEventListener('load', this.boundSetVideoSize);
    window.addEventListener('resize', this.boundSetVideoSize);
  }

  _detachEvents () {
    if (!this.context.presenter) {
      window.removeEventListener('keydown', this.boundHandleKeyPress);
    }
    window.removeEventListener('load', this.boundSetVideoSize);
    window.removeEventListener('resize', this.boundSetVideoSize);
  }

  _handleKeyPress (evt) {
    const event = window.event ? window.event : evt;
    // space
    if (event.keyCode === 32) {
      this.playPause.call(this, evt);
    }
  }

  playPause (evt) {
    const { session, i18n: _ } = this.context;
    const videoState = (session && session.videoState)
                       ? session.videoState : {};

    evt.preventDefault();
    evt.stopPropagation();
    if (evt.nativeEvent) {
      evt.nativeEvent.stopPropagation();
    }

    if (videoState.finished) {
      this.setVideoState({
        playing: false,
        started: false,
        finished: false
      });
      this.buttonLabel = _('Replay');
      setTimeout(() => {
        this.setVideoState({
          playing: true,
          started: true,
          finished: false
        });
        setTimeout(() => {
          this.buttonLabel = null;
        }, 500);
      }, 1000);
      return;
    }
    this.setVideoState({
      playing: !videoState.playing,
      started: true,
      finished: false
    });
  }

  videoEnd () {
    this.setVideoState({
      playing: false,
      finished: true
    });
  }

  render () {
    const { youTubeId, lowRes, presentation } = this.props;
    const { presenter, session, deckState: { deviceRole }, i18n: _ } = this.context;
    const { configuration, mode } = session;
    const videoState = (session && session.videoState)
                       ? session.videoState : {};
    const thumbnailUrl = lowRes
      ? `//img.youtube.com/vi/${youTubeId}/hqdefault.jpg`
      : `//i.ytimg.com/vi/${youTubeId}/maxresdefault.jpg`;

    if (presentation && mode) {
      // Don't show video when in a mode in the HUD
      return <div />;
    }

    if (presenter) {
      return <img src={thumbnailUrl} />;
    }

    const isDualScreen = (configuration === 'dualScreen');
    if (isDualScreen && deviceRole === 'remoteControl') {
      return (
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center'
          }}>
          <VideoRemoteControlButton />
        </div>
      );
    }

    let playbackState = 'unstarted';
    if (!this.state.startingUp) {
      if (videoState.playing) {
        playbackState = 'playing';
      }
      if (!videoState.playing && videoState.started) {
        playbackState = 'paused';
      }
    }

    let buttonLabel = videoState.finished ? _('Replay') : _('Play');
    if (this.buttonLabel) {
      // We need to override the button label for when Replay
      // is clicked
      buttonLabel = this.buttonLabel;
    }

    let youTubePlayer = <div />;
    if (this.state.videoWidth && this.state.videoHeight) {
      youTubePlayer = (
        <div
            className="youtube-sized-container"
            style={{
              width: `${this.state.videoWidth}px`,
              height: `${this.state.videoHeight}px`,
              position: 'relative'
            }}>
          <div
            className="youtube-iframe-wrapper"
            style={{
              position: 'absolute',
              opacity: videoState.started && !videoState.finished ?
                       0 : 1,
              transition: 'opacity 1s ease-in',
              width: `${this.state.videoWidth}px`,
              height: `${this.state.videoHeight}px`,
              background: `url(${thumbnailUrl}) no-repeat center/cover`,
              display: mobile || videoState.playing ? 'none' : 'block'
            }} />
          <div
            className="youtube-glass-overlay"
            style={{
              width: `${this.state.videoWidth}px`,
              height: `${this.state.videoHeight}px`,
              position: 'absolute',
              display: mobile || videoState.playing ? 'none' : 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onClick={::this.playPause}>
            <div className="youtube-button">
              <button
                className="btn btn-lg ppa-icon-play"
                style={{
                  opacity: videoState.playing ? 0 : 1,
                  cursor: videoState.playing ? 'none' : 'pointer',
                  transition: 'opacity 0.3s ease-in'
                }}>
                <span className="text-hide">{buttonLabel}</span>
              </button>
            </div>
          </div>
          { !videoState.finished || mobile ?
            <YoutubePlayer
              width={`${this.state.videoWidth}px`}
              height={`${this.state.videoHeight}px`}
              videoId={youTubeId}
              playbackState={playbackState}
              onPlay={::this.onPlay}
              onPause={::this.onPause}
              onEnd={::this.videoEnd}
              ref="youtubePlayer"
              configuration={{
                showinfo: 0,
                controls: 1,
                rel: 0,
                disablekb: 1,
                modestbranding: 1,
                iv_load_policy: 3
              }}/>
            :
            <div
                className="youtube-placeholder"
                style={{
                  width: `${this.state.videoWidth}px`,
                  height: `${this.state.videoHeight}px`
                }} />
          }
        </div>
      );
    }

    return (
      <div
        className="youtube-fullscreen-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100vh'
        }}>
        <div
            className="youtube-video-container"
            style={styles.videoContainer}
            ref="videoContainer">
          { this.state.videoWidth && this.state.videoHeight ?
            youTubePlayer
            : ''
          }
        </div>

        {/*
        <div style={{marginBottom: '20px'}}>State: {playbackState}</div>
        */}

      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./src/views/session/presentation/components/youTube.js